import * as React from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { Formik, FormikHelpers, FormikValues } from 'formik';
import { Chip, IconButton, InputAdornment, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { VisibilityOff, Visibility } from '@mui/icons-material';
import Style from '../../utils/styles/Style';
import PAGE_ROUTES, { API_ROUTES, getIp } from '../../utils/constant';
import { setAuth } from '../../hooks/http-server';
import { toast } from 'react-toastify';
import axios from 'axios';
import { MuiOtpInput } from 'mui-one-time-password-input';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export default function Login() {
  const [isLoading, setIsLoading] = React.useState(false);
  const [isEmailVerified, setIsEmailVerified] = React.useState(true);
  const [Token, setToken] = React.useState<string | null>(null);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = React.useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    password: Yup.string().required('Password is required'),
  });
  // Validation schema using Yup
  const validationSchemaForOtp = Yup.object({
    otp: Yup.string()
      .required('OTP is required')
      .matches(/^\d{4}$/, 'OTP must be exactly 4 digits'),
  });

  interface FormValues {
    email: string;
    password: string;
  }

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const goToForget = () => {
    navigate(PAGE_ROUTES.FORGETPASSWORD);
  };

  return (
    <Box>
      <Container
        component="main"
        sx={Style.LoginForm?.SubContainer}
        maxWidth="xs"
      >
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {isEmailVerified ? (
            <Formik
              initialValues={{ email: '', password: '' }}
              validationSchema={validationSchema}
              onSubmit={async (
                values: FormikValues,
                { resetForm }: FormikHelpers<FormValues>
              ) => {
                // Handle form submission
                setIsLoading(true);

                try {
                  const ip = await getIp();
                  const resData: any = {
                    email: values.email,
                    password: values.password,
                  };
                  if (ip) {
                    resData.ipAddress = ip;
                  }

                  const response = await axios.post(
                    `${process.env.REACT_APP_BASE_URL}${API_ROUTES.Login}`,
                    resData
                  );

                  const user = response?.data?.data;

                  setIsEmailVerified(user?.isEmailVerified);
                  setToken(user?.authTokens?.accessToken);

                  if (user?.isEmailVerified) {
                    const idToken = user?.authTokens.accessToken;
                    const refreshToken = user?.authTokens.refreshToken;
                    setAuth({
                      Token: idToken,
                      RefreshToken: refreshToken,
                      Email: user.email || '',
                      Name: user.name || '',
                      Phone: user.phoneNo || '',
                      roleId: user.roleId || '',
                      id: user.id || '',
                      deviceToken: user.deviceToken || '',
                    });

                    localStorage.setItem('refreshToken', refreshToken);
                    navigate(PAGE_ROUTES.GAMES);
                    toast.success('Login Successfully', {
                      position: 'bottom-right',
                    });
                  }
                  setIsLoading(false);
                } catch (error: any) {
                  setIsLoading(false);
                  console.error('Error in Loging', error.response.data.message);
                  toast.error(error.response.data.message, {
                    position: 'bottom-right',
                  });
                }
              }}
            >
              {({
                errors,
                touched,
                handleSubmit,
                handleBlur,
                handleChange,
                isSubmitting,
              }) => (
                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  noValidate
                  sx={{ mt: 1 }}
                >
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    onChange={handleChange}
                    autoComplete="email"
                    error={touched.email && !!errors.email}
                    helperText={touched.email && errors.email}
                  />
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    onChange={handleChange}
                    onBlur={handleBlur}
                    name="password"
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    id="password"
                    autoComplete="current-password"
                    error={touched.password && !!errors.password}
                    helperText={touched.password && errors.password}
                  />
                  <Typography
                    fontWeight={600}
                    color="#1976d2"
                    onClick={goToForget}
                    sx={{ cursor: 'pointer', display: 'inline' }}
                  >
                    Forget Password?
                  </Typography>
                  <LoadingButton
                    loading={isLoading}
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 2 }}
                  >
                    Sign In
                  </LoadingButton>
                </Box>
              )}
            </Formik>
          ) : (
            <Formik
              initialValues={{ otp: '' }}
              validationSchema={validationSchemaForOtp}
              onSubmit={async (values: FormikValues, { setSubmitting }) => {
                setIsLoading(true);

                try {
                  const ip = await getIp();
                  const resData: any = {
                    otp: values.otp,
                    token: Token,
                  };
                  if (ip) {
                    resData.ipAddress = ip;
                  }
                  const response = await axios.post(
                    `${process.env.REACT_APP_BASE_URL}${API_ROUTES.emailVerify}`,
                    resData
                  );
                  const user = response?.data?.data;
                  if (user?.isEmailVerified) {
                    const idToken = user?.authTokens.accessToken;
                    const refreshToken = user?.authTokens.refreshToken;
                    setAuth({
                      Token: idToken,
                      RefreshToken: refreshToken,
                      Email: user.email || '',
                      Name: user.name || '',
                      Phone: user.phoneNo || '',
                      roleId: user.roleId || '',
                      id: user.id || '',
                      deviceToken: user.deviceToken || '',
                    });

                    localStorage.setItem('refreshToken', refreshToken);
                    navigate(PAGE_ROUTES.GAMES);
                    toast.success('Login Successfully', {
                      position: 'bottom-right',
                    });
                  }

                  setIsLoading(false);
                } catch (error: any) {
                  setIsLoading(false);
                  console.error(
                    'Error in Logging:',
                    error.response.data.message
                  );
                  toast.error(error.response.data.message, {
                    position: 'bottom-right',
                  });
                }

                setSubmitting(false);
              }}
            >
              {({
                errors,
                touched,
                handleSubmit,
                handleChange,
                handleBlur,
                values,
              }) => (
                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  noValidate
                  sx={{ mt: 1 }}
                >
                  <Chip
                    icon={<InfoOutlinedIcon />}
                    label="Please check your email for the OTP and enter it here to complete your action."
                  />
                  <MuiOtpInput
                    sx={{ marginTop: 5 }}
                    value={values.otp}
                    onChange={(value) =>
                      handleChange({ target: { name: 'otp', value } })
                    }
                    onBlur={handleBlur}
                    length={4}
                    autoFocus
                    validateChar={(character: string) => /^\d$/.test(character)} // Allow only numeric characters
                  />
                  {errors.otp && touched.otp && (
                    <Box sx={{ color: 'red', fontSize: '0.875rem', mt: 1 }}>
                      {typeof errors.otp === 'string'
                        ? errors.otp
                        : JSON.stringify(errors.otp)}
                    </Box>
                  )}

                  <LoadingButton
                    loading={isLoading}
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 2, textTransform: 'none' }}
                    disabled={isLoading}
                  >
                    Verify OTP
                  </LoadingButton>
                </Box>
              )}
            </Formik>
          )}
        </Box>
      </Container>
    </Box>
  );
}
